/**
 * Open one deck item and see all of it.
 *
 * WHY. Every row on this board is a summary: a commitment's text is clamped, a chase shows the
 * account and hides the drafted email under it, a judgement question shows a sentence and not the
 * note it is about. He asked for the rows to open, so he can read the whole thing and act on it
 * without hunting for it somewhere else.
 *
 * THE ACTIONS ARE THE SAME ONES THE ROW ALREADY HAS. This surface invents no capability: if a card
 * offers Done and Discuss, so does the panel, with room for their real labels and a line saying
 * what each will do. A modal that could do more than the row behind it would make the board a
 * summary of a different system.
 *
 * COLD, WHERE THE FORGE IS WARM. Both are plated modals on the same grid and chamfer, and they are
 * deliberately different colours: the forge is amber and is where a BROKEN thing is taken, this is
 * the deck's own blue and is where ordinary work is read. Colour carries the distinction so the
 * two never read as the same event.
 *
 * ACTING CLOSES IT. Every action here mutates and the caller reloads the payload, so the item
 * behind this panel is usually gone a moment later. Leaving it open would show a stale copy of
 * something already answered, which is the exact state the deck exists to prevent.
 */

import { useEffect, useRef, type JSX } from 'react';
import { Icon, type IconName } from './Icon.js';

export interface DeckDetailAction {
  label: string;
  icon: IconName;
  /** What it will do, in his words. Shown under the label; the card has no room for it. */
  hint?: string;
  onClick: () => void;
  /** Kept open for an action that does not resolve the item, e.g. copying text. */
  keepOpen?: boolean;
}

export interface DeckDetailItem {
  /** The eyebrow: Action, Judgement, Follow up, Bounty. */
  kind: string;
  tone: 'act' | 'soon' | 'judge';
  /** Never truncated here. That is the point of the panel. */
  title: string;
  /** The one-line context the row shows. */
  meta: string;
  provenance: string;
  /** Long text: a drafted email, a reason, an excerpt. Whitespace is preserved. */
  body?: string;
  /** Named facts, rendered as a definition list. Empty values are dropped by the caller. */
  facts?: { label: string; value: string }[];
  actions: DeckDetailAction[];
}

export function DeckDetail({
  item,
  onClose,
}: {
  item: DeckDetailItem;
  onClose: () => void;
}): JSX.Element {
  const closeRef = useRef<HTMLButtonElement>(null);

  /*
   * Escape closes, and focus lands on Close when it opens.
   *
   * Both matter more here than on the forge: this panel is opened dozens of times in a sitting
   * rather than once when something breaks, so reaching for the mouse to dismiss it is a cost paid
   * over and over.
   */
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const facts = (item.facts ?? []).filter((f) => f.value.trim() !== '');

  return (
    <div
      className="cdeck-detail-wrap"
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
      /* The BACKDROP only. Without the target check, a click that lands on the panel and drifts
         onto the backdrop would close it mid-selection while he is copying a draft. */
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cdeck-detail" data-tone={item.tone}>
        <div className="cdeck-detail-head">
          <span className="cdeck-detail-kind">{item.kind}</span>
          <span className="cdeck-detail-prov">{item.provenance}</span>
          <button type="button" className="cdeck-detail-close" onClick={onClose} ref={closeRef}>
            Close
          </button>
        </div>

        <h2 className="cdeck-detail-title">{item.title}</h2>
        {item.meta !== '' && <p className="cdeck-detail-meta">{item.meta}</p>}

        {facts.length > 0 && (
          <dl className="cdeck-detail-facts">
            {facts.map((f) => (
              <div key={f.label}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {item.body !== undefined && item.body.trim() !== '' && (
          <div className="cdeck-detail-body">
            <pre>{item.body}</pre>
          </div>
        )}

        <div className="cdeck-detail-actions">
          {item.actions.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={() => {
                a.onClick();
                if (a.keepOpen !== true) onClose();
              }}
            >
              <Icon name={a.icon} size={14} />
              <span>
                {a.label}
                {a.hint !== undefined && <em>{a.hint}</em>}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
